<script lang="ts">
	import { formatNumber, formatDateAuto } from 'src/util/format';

	interface Props {
		metadata: Record<string, any>;
	}
	let { metadata }: Props = $props();

	type ValueType = 'link' | 'date' | 'number' | 'chips' | 'boolean' | 'json' | 'string';

	function classify(value: unknown, key: string): ValueType {
		// TODO extract to own componen so it can also be used standalone
		if (typeof value === 'boolean') {
			return 'boolean';
		}
		if (typeof value === 'number') {
			if (
				/(?:^|[_-])(?:id|ids|code|zip|plz|iban|ean|isbn|issn|fax|tel|phone|nummer|number|nr|no)(?:[_-]|$)/i.test(
					key
				)
			) {
				return 'string';
			}
			return 'number';
		}
		if (typeof value === 'string') {
			if (/^https?:\/\//i.test(value)) {
				return 'link';
			}
			if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(value) && !isNaN(Date.parse(value))) {
				return 'date';
			}
			return 'string';
		}
		if (Array.isArray(value) && value.every((v) => typeof v !== 'object' || v === null)) {
			return 'chips';
		}

		return 'json';
	}

	const rows = $derived(
		Object.entries(metadata).map(([key, value]) => ({
			key: key.replaceAll('_', ' '),
			value,
			type: classify(value, key)
		}))
	);
</script>

<div class="meta-list">
	{#each rows as { key, value, type } (`${key}${value}`)}
		<div class="meta-row">
			<span class="key">{key}</span>
			<span class="value">
				{#if type === 'link'}
					<a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
				{:else if type === 'date'}
					{formatDateAuto(value)}
				{:else if type === 'number'}
					{formatNumber(value)}
				{:else if type === 'boolean'}
					<span class="chips">
						<span class="chip chip--bool" class:chip--true={value} class:chip--false={!value}>
							{#if value}
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="10"
									height="10"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="3"
									stroke-linecap="round"
									stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg
								>
							{:else}
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="10"
									height="10"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="3"
									stroke-linecap="round"
									stroke-linejoin="round"
									><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg
								>
							{/if}
							{value ? 'Yes' : 'No'}
						</span>
					</span>
				{:else if type === 'chips'}
					<span class="chips">
						{#each value as chip (chip)}
							<span class="chip">{chip}</span>
						{/each}
					</span>
				{:else if type === 'json'}
					<pre>{JSON.stringify(value, null, 2)}</pre>
				{:else}
					{value}
				{/if}
			</span>
		</div>
	{/each}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.meta-list {
		@include lib.flex-column();
		.meta-row {
			@include lib.flex-row();
			@include lib.flex-align(space-between);
			gap: 2rem;
			padding: 0.25rem 0.5rem;
			margin: 0 -0.5rem;
			border-radius: 0.5rem;
			font-size: 0.875rem;

			&:hover {
				background: var(--state-hover);
			}

			.key {
				font-weight: 500;
				flex: 0 0 auto;
				color: var(--text-secondary);
				text-transform: capitalize;
			}

			.value {
				text-align: right;
				color: var(--text-primary);

				a {
					color: var(--accent);
					text-decoration: underline;
					word-break: break-all;
				}

				pre {
					text-align: left;
					margin: 0;
					white-space: pre-wrap;
					word-break: break-all;
				}

				.chips {
					@include lib.flex-row();
					flex-wrap: wrap;
					gap: 0.25rem;
					justify-content: flex-end;

					.chip {
						background: var(--state-hover);
						border: 1px solid var(--border-subtle);
						border-radius: 0.5rem;
						padding: 0.125rem 0.25rem;
						font-size: 0.75rem;
						line-height: 0.75rem;

						&.chip--bool {
							display: inline-flex;
							align-items: center;
							gap: 0.25rem;
						}

						&.chip--true {
							background: color-mix(in srgb, var(--color-success, #22c55e) 15%, transparent);
							border-color: color-mix(in srgb, var(--color-success, #22c55e) 40%, transparent);
							color: var(--color-success, #16a34a);
						}

						&.chip--false {
							background: color-mix(in srgb, var(--color-error, #ef4444) 15%, transparent);
							border-color: color-mix(in srgb, var(--color-error, #ef4444) 40%, transparent);
							color: var(--color-error, #dc2626);
						}
					}
				}
			}
		}
	}
</style>
